import axios from 'axios';

const api = axios.create({ baseURL: '/' });

// Attach JWT token to every request
api.interceptors.request.use(cfg => {
    const token = localStorage.getItem('token');
    if (token) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
});

// On 401 — clear token and redirect to login (skip auth endpoints)
api.interceptors.response.use(
    res => res,
    err => {
        if (err.response?.status === 401) {
            const url = err.config?.url || '';
            if (!url.startsWith('/auth/login') && !url.startsWith('/auth/register')) {
                localStorage.removeItem('token');
                localStorage.removeItem('client');
                window.location.href = '/login';
            }
        }
        return Promise.reject(err);
    }
);

// ─── Auth ────────────────────────────────────────────────────────────────────
export const login = (email, password) =>
    api.post('/auth/login', { email, password });

export const register = (username, email, password) =>
    api.post('/auth/register', { username, email, password, _trap: '' });

export const getMe = () => api.get('/auth/me');

export const regenerateKey = () => api.post('/auth/regenerate-key');

export const acceptTerms = () => api.post('/auth/accept-terms');

// ─── SMM API v2 ──────────────────────────────────────────────────────────────
const v2 = (body) => api.post('/api/v2', body);

export const getServices = (country) =>
    v2({ action: 'services', ...(country && { country }) });

export const getBalance = () => v2({ action: 'balance' });

export const addOrder = (service, link, quantity) =>
    v2({ action: 'add', service, link, quantity });

export const getOrderStatus = (order) =>
    v2({ action: 'status', order });

export const cancelOrder = (order) =>
    v2({ action: 'cancel', order });

// ─── Deposits ────────────────────────────────────────────────────────────────
export const getDeposits = () => api.get('/api/deposits/');

export const createDeposit = (amount, currency = 'USDT') =>
    api.post('/api/deposits/create', { amount, currency });

export const getBonusTiers = () => api.get('/api/deposits/bonus-tiers');

// ─── Client Orders (own orders only, JWT) ────────────────────────────────────
export const getMyOrders = (params) => api.get('/auth/orders', { params });

// ─── Admin ───────────────────────────────────────────────────────────────────
export const adminGetClients = () => api.get('/api/admin/clients');
export const adminGetClient = (id) => api.get(`/api/admin/clients/${id}`);
export const adminCreateClient = (data) => api.post('/api/admin/clients', data);
export const adminUpdateClient = (id, data) => api.patch(`/api/admin/clients/${id}`, data);

export const adminGetOrders = (params) => api.get('/api/admin/orders', { params });

export const adminGetServices = () => api.get('/api/admin/services');

export const adminGetSettings = () => api.get('/api/admin/settings');
export const adminUpdateSetting = (key, value) =>
    api.patch(`/api/admin/settings/${key}`, { value });

export const adminSyncCapacity = () => api.post('/api/admin/sync-capacity');

export const adminGetDeposits = (params) => api.get('/api/admin/deposits', { params });
export const adminForceComplete = (id) => api.patch(`/api/admin/orders/${id}/force-complete`);
export const adminForceCancel  = (id, options = {}) => api.patch(`/api/admin/orders/${id}/force-cancel`, options);

export const adminManualPayment = (data) => api.post('/api/admin/manual-payment', data);
export const adminCreditAll    = (amount, type) => api.post('/api/admin/credit-all', { amount, type });
export const adminGetBonusTiers    = ()          => api.get('/api/admin/bonus-tiers');
export const adminCreateBonusTier  = (data)      => api.post('/api/admin/bonus-tiers', data);
export const adminUpdateBonusTier  = (id, data)  => api.patch(`/api/admin/bonus-tiers/${id}`, data);
export const adminDeleteBonusTier  = (id)        => api.delete(`/api/admin/bonus-tiers/${id}`);

// ─── Admin Tiers ────────────────────────────────────────────────────────────
export const adminGetTierAudit     = ()          => api.get('/api/admin/tier-audit');
export const adminSetElite         = (id, data)  => api.put(`/api/admin/set-elite/${id}`, data);

// ─── Admin Subscriptions ──────────────────────────────────────────────────
export const adminGetSubscriptions  = (params) => api.get('/api/admin/subscriptions', { params });
export const adminGetSubscription   = (id)     => api.get(`/api/admin/subscriptions/${id}`);

// ─── Analytics ────────────────────────────────────────────────────────────
export const adminGetAnalytics = (days = 30) => api.get('/api/admin/analytics', { params: { days } });

// ─── Wallet ──────────────────────────────────────────────────────────────────
export const getWallet = (params) => api.get('/auth/wallet', { params });

// ─── Subscriptions ───────────────────────────────────────────────────────────
export const getMySubscriptions  = ()      => api.get('/api/subscriptions/');
export const getSubscription     = (id)    => api.get(`/api/subscriptions/${id}/`);
export const exportSubscription  = (id)    => api.get(`/api/subscriptions/${id}/export`, { responseType: 'blob' });

// ─── Password Reset ───────────────────────────────────────────────────────────
export const forgotPassword = (email)           => api.post('/auth/forgot-password', { email });
export const resetPassword  = (token, password) => api.post('/auth/reset-password',  { token, password });

export default api;
